// frontend/src/pages/MockPaymentPage.jsx
import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from '../config/axios';

// ── Helpers ────────────────────────────────────────────────────────────────────

function formatCard(value) {
    return value.replace(/\D/g, '').slice(0, 16).replace(/(.{4})/g, '$1 ').trim();
}

function formatExpiry(value) {
    const digits = value.replace(/\D/g, '').slice(0, 4);
    if (digits.length >= 3) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    return digits;
}

// ── Validadores ────────────────────────────────────────────────────────────────

function validateName(v) {
    const words = v.trim().split(/\s+/);
    if (!v.trim()) return 'Ingresá el nombre del titular.';
    if (words.length < 2) return 'Ingresá nombre y apellido.';
    if (!/^[A-ZÁÉÍÓÚÜÑ\s]+$/i.test(v)) return 'Solo letras, sin números.';
    return '';
}

function validateCard(v) {
    const digits = v.replace(/\s/g, '');
    if (!digits) return 'Ingresá el número de tarjeta.';
    if (digits.length < 16) return 'La tarjeta debe tener 16 dígitos.';
    return '';
}

function validateExpiry(v) {
    if (!v || v.length < 5) return 'Ingresá la fecha (MM/AA).';
    const [mm, yy] = v.split('/').map(Number);
    if (mm < 1 || mm > 12) return 'Mes inválido.';
    const now = new Date();
    const expYear = 2000 + yy;
    const expMonth = mm;
    if (expYear < now.getFullYear() || (expYear === now.getFullYear() && expMonth < now.getMonth() + 1)) {
        return 'La tarjeta está vencida.';
    }
    return '';
}

function validateCvv(v) {
    if (!v) return 'Ingresá el CVV.';
    if (v.length < 3) return 'El CVV debe tener al menos 3 dígitos.';
    return '';
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function Field({ label, value, onChange, onBlur, placeholder, maxLength, inputMode = 'text', error, touched }) {
    const borderColor = touched
        ? error ? '#EF4444' : '#10B981'
        : 'rgba(255,255,255,0.12)';

    return (
        <div className="flex flex-col gap-1">
            <label className="text-xs font-bold uppercase tracking-widest text-white/50">{label}</label>
            <input
                type="text"
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                placeholder={placeholder}
                maxLength={maxLength}
                inputMode={inputMode}
                autoComplete="off"
                className="w-full rounded-xl px-4 py-3 text-white font-semibold text-sm outline-none
                           placeholder-white/20 transition-colors"
                style={{
                    backgroundColor: 'rgba(255,255,255,0.07)',
                    border: `1.5px solid ${borderColor}`,
                }}
                onFocus={e => e.target.style.borderColor = '#2284DF'}
            />
            {touched && error && (
                <p className="text-xs font-semibold mt-0.5" style={{ color: '#FCA5A5' }}>{error}</p>
            )}
        </div>
    );
}

function OutcomeButton({ onClick, disabled, loading, variant, icon, label, sublabel }) {
    const styles = {
        approved: {
            bg: 'rgba(16,185,129,0.12)',
            border: '#10B981',
            hoverBg: 'rgba(16,185,129,0.22)',
            text: '#10B981',
        },
        rejected: {
            bg: 'rgba(239,68,68,0.12)',
            border: '#EF4444',
            hoverBg: 'rgba(239,68,68,0.22)',
            text: '#EF4444',
        },
        pending: {
            bg: 'rgba(234,179,8,0.12)',
            border: '#EAB308',
            hoverBg: 'rgba(234,179,8,0.22)',
            text: '#EAB308',
        },
    };
    const s = styles[variant];

    return (
        <button
            onClick={onClick}
            disabled={disabled}
            className="flex flex-col items-center gap-1.5 rounded-2xl px-4 py-4 w-full
                       font-bold transition-all duration-150 hover:scale-[1.03] active:scale-[0.97]
                       disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100"
            style={{ backgroundColor: s.bg, border: `2px solid ${s.border}`, color: s.text }}
        >
            <span className="text-2xl">{loading ? '⏳' : icon}</span>
            <span className="text-sm font-extrabold uppercase tracking-wide">{label}</span>
            <span className="text-xs font-normal opacity-70">{sublabel}</span>
        </button>
    );
}

// ── Main Component ─────────────────────────────────────────────────────────────

const MockPaymentPage = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const params = new URLSearchParams(location.search);
    const preferenceId = params.get('preferenceId');
    const amount = parseInt(params.get('amount') || '0');
    const total = amount * 10;

    const [cardName, setCardName] = useState('');
    const [cardNumber, setCardNumber] = useState('');
    const [cardExpiry, setCardExpiry] = useState('');
    const [cardCvv, setCardCvv] = useState('');
    const [touched, setTouched] = useState({ name: false, number: false, expiry: false, cvv: false });
    const [loading, setLoading] = useState(null); // 'approved' | 'rejected' | 'pending'
    const [error, setError] = useState('');

    // Errores por campo
    const fieldErrors = {
        name:   validateName(cardName),
        number: validateCard(cardNumber),
        expiry: validateExpiry(cardExpiry),
        cvv:    validateCvv(cardCvv),
    };
    const isFormValid = Object.values(fieldErrors).every(e => e === '');

    const touch = (field) => setTouched(prev => ({ ...prev, [field]: true }));

    const handleOutcome = async (outcome) => {
        // Marcar todos como tocados para mostrar errores
        setTouched({ name: true, number: true, expiry: true, cvv: true });
        if (!isFormValid) return;

        if (!preferenceId) {
            setError('No se encontró el ID de preferencia en la URL.');
            return;
        }
        setLoading(outcome);
        setError('');
        try {
            const response = await axios.post('/payments/mock-process', { preferenceId, outcome });
            if (response.data.success) {
                window.location.href = response.data.redirectUrl;
            } else {
                setError(response.data.message || 'Error al procesar');
                setLoading(null);
            }
        } catch (err) {
            console.error(err);
            setError('Error de conexión. Intentá de nuevo.');
            setLoading(null);
        }
    };

    return (
        <div
            className="min-h-screen flex items-center justify-center px-4 py-12"
            style={{ backgroundColor: '#0A1929' }}
        >
            <div className="w-full max-w-md">

                {/* ── Header ── */}
                <div className="text-center mb-8">
                    <div className="inline-flex items-center gap-2 mb-4 px-4 py-1.5 rounded-full font-bold text-xs uppercase tracking-widest"
                        style={{ backgroundColor: 'rgba(252,229,3,0.12)', border: '1.5px solid #FCE503', color: '#FCE503' }}>
                        <span>⚠</span> Modo de prueba
                    </div>
                    <h1 className="text-2xl font-extrabold text-white mb-1">Pago Simulado</h1>
                    <p className="text-white/50 text-sm">
                        Este checkout es ficticio — no se realiza ningún cobro real.
                    </p>
                </div>

                {/* ── Card ── */}
                <div className="rounded-[24px] overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.5)]"
                    style={{ backgroundColor: '#0E2238', border: '2px solid rgba(255,255,255,0.1)' }}>

                    {/* Resumen */}
                    <div className="px-7 pt-7 pb-5"
                        style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                        <p className="text-xs font-bold uppercase tracking-widest text-white/40 mb-3">Resumen</p>
                        <div className="flex justify-between items-center">
                            <div>
                                <p className="text-white font-bold text-base">Paquete de {amount} Tickets</p>
                                <p className="text-white/50 text-xs mt-0.5">ManoAMano · Torneos</p>
                            </div>
                            <div className="text-right">
                                <p className="text-[#FCE503] font-extrabold text-xl">${total} ARS</p>
                                <p className="text-white/40 text-xs">${10} c/u</p>
                            </div>
                        </div>
                    </div>

                    {/* Formulario ficticio */}
                    <div className="px-7 py-6 flex flex-col gap-4"
                        style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                        <p className="text-xs font-bold uppercase tracking-widest text-white/40">Datos de tarjeta</p>

                        <Field
                            label="Titular"
                            value={cardName}
                            onChange={e => setCardName(e.target.value.toUpperCase())}
                            onBlur={() => touch('name')}
                            placeholder="NOMBRE APELLIDO"
                            error={fieldErrors.name}
                            touched={touched.name}
                        />
                        <Field
                            label="Número"
                            value={cardNumber}
                            onChange={e => setCardNumber(formatCard(e.target.value))}
                            onBlur={() => touch('number')}
                            placeholder="0000 0000 0000 0000"
                            inputMode="numeric"
                            error={fieldErrors.number}
                            touched={touched.number}
                        />
                        <div className="grid grid-cols-2 gap-4">
                            <Field
                                label="Vencimiento"
                                value={cardExpiry}
                                onChange={e => setCardExpiry(formatExpiry(e.target.value))}
                                onBlur={() => touch('expiry')}
                                placeholder="MM/AA"
                                inputMode="numeric"
                                error={fieldErrors.expiry}
                                touched={touched.expiry}
                            />
                            <Field
                                label="CVV"
                                value={cardCvv}
                                onChange={e => setCardCvv(e.target.value.replace(/\D/g, '').slice(0, 4))}
                                onBlur={() => touch('cvv')}
                                placeholder="123"
                                inputMode="numeric"
                                error={fieldErrors.cvv}
                                touched={touched.cvv}
                            />
                        </div>

                        <p className="text-xs text-white/25 text-center mt-1">
                            Los datos ingresados son ignorados — es solo un formulario de prueba.
                        </p>
                    </div>

                    {/* Error */}
                    {error && (
                        <div className="mx-7 mt-5 rounded-xl px-4 py-3 text-sm font-semibold"
                            style={{ backgroundColor: 'rgba(239,68,68,0.12)', border: '1.5px solid #EF4444', color: '#FCA5A5' }}>
                            {error}
                        </div>
                    )}

                    {/* Botones de resultado */}
                    <div className="px-7 py-6 flex flex-col gap-3">
                        <p className="text-xs font-bold uppercase tracking-widest text-white/40 mb-1">
                            Simular resultado
                        </p>
                        <div className="grid grid-cols-3 gap-3">
                            <OutcomeButton
                                variant="approved"
                                icon="✅"
                                label="Aprobar"
                                sublabel="Tickets acreditados"
                                disabled={!!loading}
                                loading={loading === 'approved'}
                                onClick={() => handleOutcome('approved')}
                            />
                            <OutcomeButton
                                variant="rejected"
                                icon="❌"
                                label="Rechazar"
                                sublabel="Pago fallido"
                                disabled={!!loading}
                                loading={loading === 'rejected'}
                                onClick={() => handleOutcome('rejected')}
                            />
                            <OutcomeButton
                                variant="pending"
                                icon="⏳"
                                label="Pendiente"
                                sublabel="En revisión"
                                disabled={!!loading}
                                loading={loading === 'pending'}
                                onClick={() => handleOutcome('pending')}
                            />
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="px-7 pb-6">
                        <button
                            onClick={() => navigate('/recharge')}
                            disabled={!!loading}
                            className="w-full text-center text-sm text-white/30 hover:text-white/60
                                       transition-colors disabled:pointer-events-none"
                        >
                            ← Volver a la recarga
                        </button>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default MockPaymentPage;
